import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, MapPin, Phone } from "lucide-react";
import { applySeoCustom } from "../lib/seo";
import { CONTACT, LOCATIONS } from "../data/site";
import { PRO_CASE_STUDIES } from "../data/prosPages";
import { MaskedLines, Reveal, SectionHeading } from "../components/site/Reveal";
import { CTABand } from "../components/site/CTABand";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { FaqJsonLd } from "../components/site/BrandModules";

const splitLines = (text) => {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (t.length <= 18) return [t];
  const n = t.length > 42 ? 3 : 2;
  const budget = Math.ceil(t.length / n);
  const lines = [];
  let cur = "";
  t.split(" ").forEach((w) => {
    if (cur && (cur + " " + w).length > budget && lines.length < n - 1) { lines.push(cur); cur = w; }
    else cur = cur ? cur + " " + w : w;
  });
  if (cur) lines.push(cur);
  return lines;
};

const API = process.env.REACT_APP_BACKEND_URL;
const CV = "/files";

const PILLARS = [
  { t: "Premium systems", d: "Origin, Sunflex, Cortizo, VELFAC, Residence Collection and Fortis, specified to your home, never a single catalogue." },
  { t: "Technical advice", d: "Senior surveyors advise on sightlines, thermal performance, security and planning context before anything is ordered." },
  { t: "Experienced installation", d: "Our own in-house fitting teams, never subcontractors, detail every reveal to showroom standard." },
  { t: "Local projects", d: "Documented installations across Surrey, Berkshire and Hampshire, with references available on request." },
  { t: "Showrooms nearby", d: "Two Surrey locations with full-size doors and windows: Fairoaks Airport, Chobham and Longacres, Bagshot." },
];

const PRODUCTS = (town) => [
  { label: "Aluminium Windows", to: "/windows/aluminium-windows/", img: `${CV}/obgImage-8222.jpg`, alt: `Slim-frame aluminium windows installed by Cedar Views near ${town}` },
  { label: "Sliding Doors", to: "/doors/sliding-doors/", img: `${CV}/obgImage-8189.jpg`, alt: `Panoramic aluminium sliding doors for homes in ${town}` },
  { label: "Bifold Doors", to: "/doors/bifold-doors/", img: "/origin/files/small-1348.jpg", alt: `Origin bifold doors opening a ${town} home to the garden` },
  { label: "Entrance Doors", to: "/entrance-doors/", img: `${CV}/obgImage-8337.png`, alt: `Secure Fortis aluminium entrance door for a ${town} property` },
  { label: "Internal Doors", to: "/doors/internal-doors/", img: `${CV}/obgImage-8167.jpg`, alt: `Origin internal glazed doors in a ${town} interior` },
  { label: "Roof Windows & Lanterns", to: "/roof-windows/", img: "/images/roof-lantern.jpg", alt: `Aluminium roof lantern flooding a ${town} extension with light` },
];

const AUDIENCES = [
  { t: "Homeowners", d: "Design-led renovations and replacements", to: "/contact/" },
  { t: "Architects", d: "Specification support and technical detail", to: "/architects/" },
  { t: "Developers", d: "Programme-certain glazing packages", to: "/developers/" },
  { t: "Builders", d: "Coordinated supply and installation", to: "/quantity-surveyors/" },
];

export default function AreaPage() {
  const { slug } = useParams();
  const [area, setArea] = useState(null);
  const [error, setError] = useState(false);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 120]);

  useEffect(() => {
    setArea(null);
    setError(false);
    fetch(`${API}/api/areas/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setArea)
      .catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (area) applySeoCustom({ title: area.seo_title, description: area.seo_description, path: `/areas/${area.slug}/` });
  }, [area]);

  if (error) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="area-not-found">
        <div className="text-center">
          <h1 className="font-display text-3xl text-white">Area not found</h1>
          <Link to="/areas/" className="btn-ghost mt-8 inline-flex">View all areas <ArrowRight size={14} /></Link>
        </div>
      </main>
    );
  }
  if (!area) return <main className="min-h-[60vh]" data-testid="area-loading" />;

  const { town, county } = area;
  const products = area.products?.length ? area.products : PRODUCTS(town);
  const projects = area.projects?.length
    ? area.projects
    : PRO_CASE_STUDIES.slice(0, 3).map((cs) => ({ img: cs.img, title: cs.t, loc: cs.loc, to: "/projects/" }));

  return (
    <main data-testid="area-page">
      {/* HERO */}
      <section className="relative min-h-[76vh] overflow-hidden flex items-center pt-32 pb-16">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={area.hero_image || `${CV}/obgImage-8320.jpg`} alt={area.hero_alt || `Premium aluminium glazing installed by Cedar Views near ${town}, ${county}`} className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050A15]/92 via-[#050A15]/65 to-[#050A15]/35" />
        </motion.div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full">
          <Reveal delay={0.1}>
            <p className="overline-x mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/areas/" className="hover:text-white transition-colors">Areas</Link>
              <span className="mx-2">/</span>
              {town}
            </p>
          </Reveal>
          <MaskedLines as="h1" lines={area.h1 ? splitLines(area.h1) : ["Premium Aluminium", "Windows & Doors", `in ${town}`]} delay={0.2}
            className="font-display tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02] max-w-4xl" />
          <Reveal delay={0.55} className="mt-7">
            <h2 className="text-base md:text-lg text-white/75 max-w-xl font-normal">
              {area.hero_sub || `Designed, supplied and installed across ${town} and ${county} by one in-house Cedar Views team.`}
            </h2>
          </Reveal>
          <Reveal delay={0.7} className="mt-9 flex flex-wrap gap-4">
            <Link to="/contact/" data-testid="area-hero-cta" className="btn-solid">{area.cta?.label || "Book a consultation"} <ArrowRight size={14} /></Link>
            <a href="#enquiry" className="btn-ghost">Quick enquiry</a>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-[#F4F4F2]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <SectionHeading number="01" kicker={`${town} · ${county}`} title={area.intro_title || `Cedar Views in ${town}.`} light />
          <Reveal className="-mt-8 space-y-5">
            {area.intro.map((p) => (
              <p key={p.slice(0, 40)} className="leading-relaxed text-lg" style={{ color: "#3a4a58" }}>{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* WHY CEDAR VIEWS */}
      <section className="border-t hairline bg-[#070E1D]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionHeading number="02" kicker="Why Cedar Views" title={`A premium glazing partner for ${town}.`} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {PILLARS.map((w, i) => (
              <Reveal key={w.t} delay={(i % 3) * 0.1} className="bg-[#070E1D] p-9 lg:p-11">
                <span className="font-mono-x text-xs text-[#5B7F9E]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display font-medium text-xl text-white mt-8 mb-4">{w.t}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{w.d}</p>
              </Reveal>
            ))}
            <Reveal delay={0.2} className="relative min-h-[260px] overflow-hidden">
              <img src={`${CV}/obgImage-8233.jpg`} alt={`Origin aluminium windows and doors available to homeowners in ${town}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="bg-[#070E1D] border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 lg:pb-32">
          <SectionHeading number="03" kicker="The collection" title={`Products we install in ${town}.`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p, i) => (
              <Reveal key={p.to} delay={(i % 3) * 0.08}>
                <Link to={p.to} data-testid={`area-product-${i}`} className="group block relative overflow-hidden min-h-[280px]">
                  <img src={p.img} alt={p.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050A15]/90 via-[#050A15]/25 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between">
                    <h3 className="font-display font-medium text-xl text-white">{p.label}</h3>
                    <ArrowRight size={16} className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all mb-1" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="bg-white border-y hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <SectionHeading number="04" kicker="Who we work with" title={`Serving all of ${town}.`} light />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 -mt-2">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.t} delay={i * 0.08}>
                <Link to={a.to} data-testid={`area-audience-${i}`} className="group block border border-[#24333F]/15 p-8 h-full hover:border-[#24333F] transition-colors">
                  <h3 className="font-display uppercase tracking-tight text-xl" style={{ color: "#24333F" }}>{a.t}</h3>
                  <p className="text-sm mt-3" style={{ color: "#3a4a58" }}>{a.d}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: "#24333F" }}>
                    Find out more <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL PROJECTS */}
      <section className="bg-[#070E1D]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionHeading number="05" kicker="Project journal" title={`Recent work near ${town}.`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((cs, i) => (
              <Reveal key={cs.title || i} delay={i * 0.08}>
                <Link to={cs.to || "/projects/"} data-testid={`area-project-${i}`} className="group block relative overflow-hidden aspect-square">
                  <img src={cs.img} alt={`${cs.title} — Cedar Views installation near ${town}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050A15]/90 via-[#050A15]/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6">
                    <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-white/50">{cs.loc}</p>
                    <h3 className="font-display font-medium text-lg text-white mt-1.5 leading-snug">{cs.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <Link to="/projects/" data-testid="area-all-projects" className="btn-ghost">View the project journal <ArrowRight size={14} /></Link>
          </Reveal>
        </div>
      </section>

      {/* WHY HOMEOWNERS CHOOSE */}
      <section className="bg-[#F4F4F2]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <SectionHeading number="06" kicker="Local knowledge" title={area.local_title || `Why homeowners in ${town} choose Cedar Views.`} light />
          <Reveal className="-mt-8 space-y-5">
            {area.local.map((p) => (
              <p key={p.slice(0, 40)} className="leading-relaxed text-lg" style={{ color: "#3a4a58" }}>{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-t hairline">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <SectionHeading number="07" kicker={`Common questions in ${town}`} title="Frequently asked questions." light />
          <Accordion type="single" collapsible className="w-full -mt-6" data-testid="area-faq">
            {area.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-[#31465A]/15">
                <AccordionTrigger data-testid={`area-faq-trigger-${i}`} className="text-left font-display text-base lg:text-lg hover:no-underline py-6" style={{ color: "#24333F" }}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed pb-6" style={{ color: "#3a4a58" }}>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {area.faqs?.length > 0 && <FaqJsonLd faqs={area.faqs} />}
        </div>
      </section>

      {/* SHOWROOM CTA */}
      <section className="bg-[#31465A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="overline-x">Visit us from {town}</p>
            <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl text-white mt-4">{area.cta?.heading || "See it in person before you decide."}</h2>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/contact/" data-testid="area-showroom-cta" className="btn-dark">Book an appointment <ArrowRight size={14} /></Link>
              <a href={CONTACT.phoneHref} className="btn-ghost"><Phone size={14} /> {CONTACT.phone}</a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <ul className="space-y-5">
              {LOCATIONS.map((loc) => (
                <li key={loc.id} className="flex items-start gap-4 text-white/85 text-sm leading-relaxed">
                  <MapPin size={16} className="text-[#EFE6D0] mt-0.5 shrink-0" />
                  <span><strong className="text-white">{loc.place}</strong><br />{loc.address.join(", ")}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* RELATED */}
      <section className="bg-[#070E1D] border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <Reveal>
            <p className="overline-x mb-6">Nearby areas we serve</p>
            <div className="flex flex-wrap gap-3" data-testid="area-nearby">
              {(area.nearby_resolved || []).map((n) => (
                <Link key={n.slug} to={`/areas/${n.slug}/`} data-testid={`area-nearby-${n.slug}`}
                  className="px-5 py-2.5 border border-white/20 text-white/80 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-white hover:text-[#0B162C] transition-colors">
                  {n.town}
                </Link>
              ))}
              <Link to="/areas/" className="px-5 py-2.5 border border-white/20 text-white/80 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-white hover:text-[#0B162C] transition-colors inline-flex items-center gap-2">
                All areas <ArrowRight size={12} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand product="Windows & Doors" />
    </main>
  );
}
